import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Check, Plus, Tag as TagIcon } from 'lucide-react';
import { createTag, fetchTags, matchTags, setWorkItemTags, TAGS_CHANGED_EVENT, type TagDto } from '../../lib/tags';
import { cn } from '../../lib/utils';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useOverlays } from './overlay-context';

/** The org's tags, kept fresh when any picker creates or assigns one. */
export function useTags(): TagDto[] {
  const [tags, setTags] = useState<TagDto[]>([]);
  useEffect(() => {
    let cancelled = false;
    const load = () =>
      fetchTags()
        .then((items) => {
          if (!cancelled) setTags(items);
        })
        .catch(() => undefined);
    void load();
    window.addEventListener(TAGS_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(TAGS_CHANGED_EVENT, load);
    };
  }, []);
  return tags;
}

export function TagChip({ tag, className }: { tag: Pick<TagDto, 'name' | 'color'>; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex max-w-[10rem] items-center gap-1 rounded-full border px-1.5 py-px text-[11px] font-medium text-foreground/80',
        className,
      )}
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} aria-hidden />
      <span className="truncate">{tag.name}</span>
    </span>
  );
}

/** Choose a work item's tags; typing a new name creates the tag for the whole org. */
export function TagPicker({
  workItemId,
  value,
  onChange,
  disabled,
  children,
  align = 'start',
}: {
  workItemId: string;
  value: string[];
  onChange?: (tagIds: string[]) => void;
  disabled?: boolean;
  children: ReactNode;
  align?: 'start' | 'end';
}) {
  const tags = useTags();
  const { toast } = useOverlays();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const selected = useMemo(() => new Set(value), [value]);
  const { matches, exact } = matchTags(tags, query);

  async function save(next: string[]) {
    setBusy(true);
    try {
      const saved = await setWorkItemTags(workItemId, next);
      onChange?.(saved);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not change the tags');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(tag: TagDto) {
    const next = selected.has(tag.id) ? value.filter((id) => id !== tag.id) : [...value, tag.id];
    await save(next);
  }

  async function create() {
    const name = query.trim();
    if (!name) return;
    setBusy(true);
    try {
      const tag = await createTag(name);
      setQuery('');
      if (!selected.has(tag.id)) await save([...value, tag.id]);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not create the tag');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverContent align={align} className="w-64 p-2" aria-label="Tags">
        <Input
          autoFocus
          className="h-8 text-sm"
          placeholder="Find or create a tag"
          aria-label="Find or create a tag"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            if (matches.length === 1 && exact) void toggle(matches[0]);
            else if (!exact) void create();
          }}
        />
        <ul className="mt-2 max-h-64 overflow-y-auto">
          {matches.map((tag) => (
            <li key={tag.id}>
              <button
                type="button"
                disabled={busy}
                onClick={() => void toggle(tag)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted disabled:opacity-60"
              >
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} aria-hidden />
                <span className="min-w-0 flex-1 truncate">{tag.name}</span>
                {selected.has(tag.id) ? <Check className="h-3.5 w-3.5 text-primary" aria-label="Selected" /> : null}
              </button>
            </li>
          ))}
          {query.trim() && !exact ? (
            <li>
              <button
                type="button"
                disabled={busy}
                onClick={() => void create()}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted disabled:opacity-60"
              >
                <Plus className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                Create “{query.trim()}”
              </button>
            </li>
          ) : null}
          {!query.trim() && tags.length === 0 ? (
            <li className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground">
              <TagIcon className="h-3.5 w-3.5" aria-hidden />
              No tags yet. Type a name to create one.
            </li>
          ) : null}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
